import { useEffect, useState } from "react";

import { AxiosError } from "axios";
import { toast } from "react-toastify";
import dayjs, { Dayjs } from "dayjs";

import { Box, Button, Divider, Fab, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Delete, Search } from "@mui/icons-material";
import { Field, Form, Formik } from "formik";

import { Analysis } from "../../../../../app/models/general/analysis";
import { PaginatedResult } from "@/app/models/default/paginated-result";
import { PoItem } from "../../../../models/general/po";

import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";

import { ErrorComponent } from "../../../../components/error/error.component";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import DialogVersion from "../../../../components/dialog/dialog.component";
import Loading from "../../../../components/loading/loading";

import { translate } from "../../../../translate/translate";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";
import { formatter } from "../../../../utils/formatter-utils";
import EditIcon from "@mui/icons-material/Edit";

type EditableColumn = "quantity" | "estimated_time_delivery" | "po_number";

const SimulationInTransitComponent = ({ analysis }: { analysis: Analysis }) => {
  const service = new BaseService<PoItem>(URLS.PO_COMPONENT);
  const analysisService = new BaseService(URLS.ANALYSIS);

  const [poComponents, setPoComponents] = useState<PoItem[]>([]);
  const [searchPO, setSearchPO] = useState("");
  const [searchStatus, setSearchStatus] = useState<string>("all");
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [openResetModal, setOpenResetModal] = useState<boolean>(false);
  const [openDeleteWaitingInvoiceModal, setOpenDeleteWaitingInvoiceModal] = useState<boolean>(false);

  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [dateTimeValue, setDateTimeValue] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const displayedColumns = ["part_number", "description", "po_number", "estimated_time_delivery", "quantity", "status"];
  const displayedColumnsShow = ["part_number", "description", "invoice", "delivery_date", "quantity", "status"];
  const dateColumns = ["estimated_time_delivery"];
  const editableColumns = ["quantity", "estimated_time_delivery", "po_number"];
  const rightAlignColumns = ["quantity", "delivery_date"];
  const dateTimeFormat = translate.language === "pt" ? "DD/MM/YYYY" : "MM/DD/YYYY";
  const searchFieldLabel = [translate.t("part_number"), `${", "}`, translate.t("description")];

  const handleSearchChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSearchPO(event.target.value as string);
  };

  const getRowKey = (_row: PoItem, index: number) => {
    return `row-${index}`;
  };

  const search = () => {
    searchWithStatus(searchStatus);
  };

  const searchWithStatus = (statusVal: string) => {
    service.clearParameters();
    service.addParameter("analysis_id", analysis.id);
    if (searchPO) service.addParameter("search", searchPO);
    if (statusVal && statusVal !== "all") service.addParameter("status", statusVal);
    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    setLoading(true);
    service
      .getAllFromListRoutePaginate("by-analysis")
      .then((response) => {
        setPagination(response.data);
        setPoComponents(response.data.results || []);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    search();
  }, []);

  useEffect(() => {
    search();
  }, [page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenResetModal = () => {
    setOpenResetModal(true);
  };

  const handleCloseResetModal = (result: unknown) => {
    if (typeof result === "boolean" && result && analysis) {
      analysisService.clearParameters();
      analysisService.deleteFromDetailRoute(analysis?.id, "reset-po")
        .then(() => {
          toast.success(translate.t("successfully_reset"));
          search();
        });
    }
    setOpenResetModal(false);
  };

  const handleOpenDeleteWaitingInvoiceModal = () => {
    setOpenDeleteWaitingInvoiceModal(true);
  };

  const handleCloseDeleteWaitingInvoiceModal = (result: unknown) => {
    if (typeof result === "boolean" && result && analysis) {
      setLoading(true);
      analysisService.clearParameters();
      analysisService.deleteFromDetailRoute(analysis?.id, "delete-waiting-invoice-po")
        .then(() => {
          toast.success(translate.t("waiting_invoice_deleted_successfully"));
          setPage(0);
          search();
        })
        .catch((error: any) => {
          ErrorComponent(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    setOpenDeleteWaitingInvoiceModal(false);
  };

  const hasWaitingInvoiceItems = poComponents.some((row: any) => {
    const isWaiting = row.status ? row.status === "WAITING_INVOICE" : !(row.po_number || row.invoice || row.original_po_number);
    const qty = Number(row.effective_quantity ?? row.quantity ?? 0);
    return isWaiting && qty > 0;
  });

  const getItemStatus = (row: PoItem) => {
    if ((row as any).status) {
      if ((row as any).status === "WAITING_INVOICE") return translate.t("waiting_invoice");
      if ((row as any).status === "IN_TRANSIT") return translate.t("material_in_transit");
    }
    const hasInvoiceOrPo = Boolean(row.po_number || (row as any).invoice || row.original_po_number);
    return hasInvoiceOrPo ? translate.t("material_in_transit") : translate.t("waiting_invoice");
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setDateTimeValue(null);
  };

  const handleConfirmEdit = (currentCell: string, column: EditableColumn, row: PoItem) => {
    if (column === "estimated_time_delivery" && (!dateTimeValue || !dateTimeValue.isValid())) {
      toast.error(translate.t("invoice_no_date_error"));
      return;
    }

    const value = column === "estimated_time_delivery"
      ? (dateTimeValue ? dateTimeValue.toISOString() : row[column])
      : (document.querySelector(`#cell-input-${currentCell}`) as HTMLInputElement)?.value;

    handleSaveField(row, value, column);
  };

  const handleDateAccept = (newValue: Dayjs | null, column: EditableColumn, row: PoItem) => {
    if (!newValue || !newValue.isValid()) {
      toast.error(translate.t("invoice_no_date_error"));
      return;
    }
    handleSaveField(
      row,
      newValue.toISOString(),
      column,
    );
  };

  const handleKeyDownSave = (e: React.KeyboardEvent, column: EditableColumn, row: PoItem) => {
    if (e.key === "Enter") {
      if (column === "estimated_time_delivery" && (!dateTimeValue || !dateTimeValue.isValid())) {
        toast.error(translate.t("invoice_no_date_error"));
        return;
      }

      const value = column === "estimated_time_delivery"
        ? (dateTimeValue ? dateTimeValue.toISOString() : row[column])
        : (e.target as HTMLInputElement).value;

      handleSaveField(row, value, column);
    }
  };

  const handleCellClick = (column: EditableColumn, row: PoItem) => {
    const index = poComponents.findIndex(r => r === row);
    setEditingCell(`${column}-${getRowKey(row, index)}`);
    if (column === "estimated_time_delivery") {
      const { effectiveValue } = getColumnConfig(column, row);
      setDateTimeValue(dayjs(effectiveValue));
    }
  };

  const renderDatePicker = (currentCell: string, column: EditableColumn, row: PoItem) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={dateTimeValue || dayjs(row[column])}
          onChange={(newValue) => setDateTimeValue(dayjs(newValue))}
          onAccept={(newValue) => handleDateAccept(newValue, column, row)}
          format={dateTimeFormat}
          slotProps={{
            textField: {
              id: `cell-input-${currentCell}`,
              variant: "standard",
              size: "small",
              fullWidth: true,
              autoFocus: true,
              onKeyDown: (e) => handleKeyDownSave(e, column, row),
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const renderTextField = (currentCell: string, column: EditableColumn, row: PoItem) => {
    const { inputType, textAlign, effectiveValue } = getColumnConfig(column, row);

    const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let v = event.target.value;
      const lang = translate.language === "en" ? "en" : "pt";

      if (lang === "en") {
        v = v.replace(/[^\d.]/g, "");

        const parts = v.split(".");
        let intPart = parts[0].replace(/^0+(?!$)/, "");
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        v = intPart;
        if (parts[1] !== undefined) {
          v += "." + parts[1].replace(/,/g, "");
        }
      } else {
        v = v.replace(/[^\d,]/g, "");

        const parts = v.split(",");
        let intPart = parts[0].replace(/^0+(?!$)/, "");
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        v = intPart;
        if (parts[1] !== undefined) {
          v += "," + parts[1].replace(/\./g, "");
        }
      }

      event.target.value = v;
    };

    return (
      <TextField
        id={`cell-input-${currentCell}`}
        autoFocus
        defaultValue={parseLocaleNumber(effectiveValue, false)}
        variant="standard"
        fullWidth
        size="small"
        type={inputType}
        inputProps={textAlign ? { style: { textAlign: textAlign as any } } : undefined}
        onChange={handleNumericChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleKeyDownSave(e, column, row)
          }
        }}
      />
    );
  };

  const handleSaveField = async (component: any, newValue: any, fieldName: EditableColumn) => {
    const originalPoComponents = [...poComponents];

    const index = poComponents.findIndex(r => r === component);
    const rowKey = getRowKey(component, index);
    const currentRow: any = poComponents[index];
    if (!currentRow) return;

    const lang = translate.language === "en" ? "en" : "pt";
    let numericString = newValue;
    let parsedValue: any;
    if (fieldName === "quantity") {
      if (typeof numericString === "string") {
        if (lang === "en") {
          numericString = numericString.replace(/,/g, "");
        } else {
          numericString = numericString.replace(/\./g, "").replace(",", ".");
        }
      }

      parsedValue = typeof numericString === "string" ? parseFloat(numericString) : numericString;
      parsedValue = isNaN(parsedValue) ? 0 : parsedValue;
    } else if (fieldName === "po_number" && typeof numericString === "string") {
      parsedValue = numericString.trim().replace(/\s*;\s*/g, ";");
    } else {
      parsedValue = numericString;
    }

    setPoComponents(prev =>
      prev.map((row: any, idx: number) => {
        if (getRowKey(row, idx) !== rowKey) return row;

        if (fieldName === "quantity") {
          const currentValue = row.effective_quantity;
          const nextOriginal = row.original_qty ?? currentValue;
          const isActuallyEdited = parsedValue !== nextOriginal;

          return {
            ...row,
            effective_quantity: parsedValue,
            original_qty: nextOriginal,
            is_quantity_edited: isActuallyEdited,
          };
        } else if (fieldName === "po_number") {
          return {
            ...row,
            po_number: parsedValue,
            is_po_number_edited: true,
          };
        } else {
          const currentValue = row.effective_estimated_time_delivery;
          const nextOriginal = row.original_estimated_time_delivery ?? currentValue;
          const isActuallyEdited = parsedValue !== nextOriginal;

          return {
            ...row,
            effective_estimated_time_delivery: parsedValue,
            original_estimated_time_delivery: nextOriginal,
            is_estimated_time_delivery_edited: isActuallyEdited,
          };
        }
      }),
    );

    setEditingCell(null);
    setDateTimeValue(null);

    try {
      const payload: any = {
        po_component_id: component.po_component_id,
      };

      if (fieldName === "quantity") {
        payload.quantity = parsedValue;
      } else if (fieldName === "po_number") {
        payload.invoice = parsedValue;
        payload.in_transit_from_delivery_date = true;
      } else {
        payload.estimated_time_delivery = parsedValue;
        payload.quantity = component.effective_quantity ?? component.quantity;
      }

      await analysisService.saveFromDetailRoute(payload, analysis?.id, "update-po");
      toast.success(translate.t("successfully_updated"));
    } catch (err) {
      setPoComponents(originalPoComponents);
      const axiosError = err as AxiosError;
      ErrorComponent(axiosError);
      toast.error(translate.t("update_failed"));
    }
  };

  const getColumnConfig = (column: string, row: any) => {
    const columnConfigs = {
      quantity: {
        isEdited: row?.is_quantity_edited,
        originalValue: parseLocaleNumber(row?.original_quantity || row?.quantity, false),
        effectiveValue: row?.effective_quantity,
        formattedValue: parseLocaleNumber(row?.effective_quantity, false),
        inputType: "text" as const,
        textAlign: "right" as const,
        fontWeight: row?.is_quantity_edited ? 600 : 400,
      },
      estimated_time_delivery: {
        isEdited: row?.is_estimated_time_delivery_edited,
        originalValue: formatter.formatDate(
          row?.original_estimated_time_delivery || row?.estimated_time_delivery,
        ),
        effectiveValue: row?.effective_estimated_time_delivery,
        formattedValue: formatter.formatDate(row?.effective_estimated_time_delivery),
        inputType: "date" as const,
        textAlign: undefined,
        fontWeight: row?.is_estimated_time_delivery_edited ? 600 : 400,
      },
      po_number: {
        isEdited: row?.is_po_number_edited,
        originalValue: "",
        effectiveValue: row?.po_number ?? "",
        formattedValue: row?.po_number ?? "",
        inputType: "text" as const,
        textAlign: undefined,
        fontWeight: row?.is_po_number_edited ? 600 : 400,
      },
    };

    return columnConfigs[column as keyof typeof columnConfigs];
  };

  const renderEditableCell = (
    column: EditableColumn,
    row: PoItem,
  ) => {
    const index = poComponents.findIndex(r => r === row);
    const currentCell = `${column}-${getRowKey(row, index)}`;

    const {
      isEdited,
      formattedValue,
      fontWeight,
    } = getColumnConfig(column, row);

    // po_number: se já tem valor e NÃO foi editado, exibe somente leitura
    const isPoNumberColumn = column === "po_number";
    if (isPoNumberColumn && formattedValue && !isEdited) {
      return (
        <Box sx={{ fontWeight, display: "flex", alignItems: "center", gap: "4px" }}>
          {formattedValue}
        </Box>
      );
    }

    if (editingCell === currentCell) {
      return (
        <Box
          display="flex"
          alignItems="center"
          width={"100%"}
          gap={0.5}
        >
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={handleCancelEdit}
            sx={{ borderColor: "#cfd8dc" }}
          >
            ✕
          </Button>

          <Button
            size="small"
            color="success"
            variant="outlined"
            onClick={() => handleConfirmEdit(currentCell, column, row)}
            sx={{ borderColor: "#cfd8dc" }}
          >
            ✓
          </Button>

          <Box flex={1}>
            {column === "estimated_time_delivery"
              ? renderDatePicker(currentCell, column, row)
              : isPoNumberColumn
                ? (
                  <TextField
                    id={`cell-input-${currentCell}`}
                    autoFocus
                    defaultValue={formattedValue || ""}
                    variant="standard"
                    fullWidth
                    size="small"
                    type="text"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleKeyDownSave(e, column, row);
                    }}
                  />
                )
                : renderTextField(currentCell, column, row)
            }
          </Box>
        </Box>
      );
    }

    return (
      <Box
        onClick={() => handleCellClick(column, row)}
        sx={{
          cursor: "pointer",
          fontWeight: fontWeight,
          display: "flex",
          alignItems: "center",
          justifyContent: isPoNumberColumn ? "flex-start" : "flex-end",
          gap: "4px",
        }}
      >
        <EditIcon
          className="eye-icon"
          sx={{ opacity: 0, transition: "opacity 0.2s", color: "#90A4AE" }}
          fontSize="small"
        />
        {formattedValue || (
          <Box component="span" sx={{ color: "#90A4AE", fontStyle: "italic", fontSize: "0.8rem" }}>
            {translate.t("invoice")}
          </Box>
        )}
      </Box>
    );
  };

  const renderCellHandler = (column: string, _index: number, row: any) => {
    if (column === "status") {
      return getItemStatus(row);
    }
    if (editableColumns.includes(column)) {
      return renderEditableCell(column as EditableColumn, row);
    }
    return row[column];
  };

  const renderCellHandlers: Record<string, typeof renderCellHandler> = {};
  displayedColumns.forEach((col) => {
    renderCellHandlers[col] = renderCellHandler;
  });

  const headerCellCustomStyleHandler = (column: string) => {
    if (rightAlignColumns.includes(column)) {
      return {
        textAlign: "right",
      };
    }
  };

  const cellCustomStyleHandler = (column: string) => {
    if (column === "quantity" || column === "estimated_time_delivery") {
      return {
        cursor: "pointer",
        color: "#3949AB",
        textAlign: "right",
        padding: "8px",
        "&:hover": {
          "& .eye-icon": {
            opacity: 1,
          },
        },
      };
    }
    if (column === "po_number") {
      return {
        padding: "8px",
        "&:hover": {
          "& .eye-icon": {
            opacity: 1,
          },
        },
      };
    }
  };

  const statusOptions = [
    { value: "all", label: translate.t("all") },
    { value: "IN_TRANSIT", label: translate.t("material_in_transit") },
    { value: "WAITING_INVOICE", label: translate.t("waiting_invoice") },
  ];

  return (
    <section>
      <Loading open={loading} />
      <Paper variant="outlined" sx={{ padding: "16px", marginBottom: "16px" }}>
        <Formik
          enableReinitialize={true}
          initialValues={{ searchValue: "" }}
          onSubmit={(_, { setSubmitting }) => {
            if (page === 0) {
              search();
            } else {
              setPage(0);
            }
            setSubmitting(false);
          }}
        >
          {({ isSubmitting }) => (
            <Form autoComplete="off">
              <Grid alignItems="center" container spacing={2} direction={"row"}>
                <Grid item xs={12} sm={4}>
                  <Field
                    id="inp-search-material"
                    name="searchValue"
                    variant="outlined"
                    label={searchFieldLabel}
                    as={TextField}
                    value={searchPO}
                    onChange={handleSearchChange}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id="status-filter-label">{translate.t("status")}</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="select-status-filter"
                      value={searchStatus}
                      onChange={(e) => {
                        const newStatus = e.target.value as string;
                        setSearchStatus(newStatus);
                        if (page === 0) {
                          searchWithStatus(newStatus);
                        } else {
                          setPage(0);
                        }
                      }}
                      label={translate.t("status")}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item>
                  <Fab size="small" type="submit" color="primary" disabled={isSubmitting}>
                    <Search id="submit-search-po-material" />
                  </Fab>
                </Grid>

                <Grid item>
                  <Divider
                    orientation="vertical"
                    variant="fullWidth"
                    sx={{ height: "40px" }}
                  />
                </Grid>

                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenResetModal}
                    startIcon={<Delete height={18} width={18} />}
                  >
                    {translate.t("reset_data")}
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={!hasWaitingInvoiceItems || loading}
                    onClick={handleOpenDeleteWaitingInvoiceModal}
                    startIcon={<Delete height={18} width={18} />}
                  >
                    {translate.t("delete_waiting_invoice_items")}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>

        <Grid item>
          <Divider
            orientation="horizontal"
            variant="fullWidth"
            sx={{ marginTop: "20px" }}
          />
        </Grid>

        <Grid sx={{ marginTop: "20px" }}>
          <TablePaginationComponent
            state={poComponents}
            displayedColumnsShow={displayedColumnsShow}
            displayedColumns={displayedColumns}
            dateColumns={dateColumns}
            count={pagination?.count}
            next={pagination?.next}
            previous={pagination?.previous}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            headerCellCustomStyleHandler={headerCellCustomStyleHandler}
            renderCellHandler={renderCellHandlers}
            cellCustomStyleHandler={cellCustomStyleHandler}
            stickyHeader
            disablePointer
          />
        </Grid>
      </Paper>

      {openResetModal && (
        <DialogVersion
          id="dialog-reset-po"
          title={translate.t("reset_data")}
          message={translate.t("confirm_reset")}
          labelButton={translate.t("confirm")}
          colorButton={"primary"}
          open={openResetModal}
          onClose={handleCloseResetModal}
        />
      )}

      {openDeleteWaitingInvoiceModal && (
        <DialogVersion
          id="dialog-delete-waiting-invoice-po"
          title={translate.t("delete_waiting_invoice_items")}
          message={translate.t("confirm_delete_waiting_invoice")}
          labelButton={translate.t("confirm")}
          colorButton={"error"}
          open={openDeleteWaitingInvoiceModal}
          onClose={handleCloseDeleteWaitingInvoiceModal}
        />
      )}
    </section>
  );
};

export default SimulationInTransitComponent;
